import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LocalizedLink as Link } from './LocalizedLink';
import { useAuth } from '../context/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { trackNavClick, trackSignupClick } from '../services/analytics';
import '../pages/LandingPage.css';

interface SiteHeaderProps {
  /**
   * 'transparent' — starts transparent, becomes solid on scroll (landing page behaviour).
   * 'solid'       — always has the solid/scrolled background (every other page).
   */
  variant?: 'transparent' | 'solid';
}

export default function SiteHeader({ variant = 'solid' }: SiteHeaderProps) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [scrolled, setScrolled] = useState(variant === 'solid');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);

  const isLandingPage = location.pathname === '/' || /^\/[a-z]{2}$/.test(location.pathname);

  /* ── Scroll listener (only for transparent variant) ── */
  useEffect(() => {
    if (variant !== 'transparent') return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [variant]);

  /* ── Smooth-scroll helper for landing page sections ── */
  const scrollTo = (id: string) => {
    setMobileMenuOpen(false);
    trackNavClick(id);
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  /** If we're on the landing page, smooth-scroll; otherwise navigate to /#section */
  const handleSectionClick = (e: React.MouseEvent, sectionId: string) => {
    if (isLandingPage) {
      e.preventDefault();
      scrollTo(sectionId);
    }
    // else: let the <Link to="/#section"> navigate normally
  };

  return (
    <nav ref={navRef} className={`lp-navbar${scrolled ? ' scrolled' : ''}`}>
      <div className="lp-container">
        {isLandingPage ? (
          <a href="#" className="lp-navbar-logo" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <img src="/logo.svg?v=2" alt="Reviio Logo" className="lp-logo-img" width={32} height={32} /><span>{` ${t('common.reviio')}`}</span></a>
        ) : (
          <Link to="/" className="lp-navbar-logo">
            <img src="/logo.svg?v=2" alt="Reviio Logo" className="lp-logo-img" width={32} height={32} /><span>{` ${t('common.reviio')}`}</span></Link>
        )}

        <ul className={`lp-navbar-links${mobileMenuOpen ? ' open' : ''}`}>
          <li>
            <Link to="/#features" onClick={(e: React.MouseEvent) => handleSectionClick(e, 'features')}>{t('common.features')}</Link>
          </li>
          <li>
            <Link to="/#how-it-works" onClick={(e: React.MouseEvent) => handleSectionClick(e, 'how-it-works')}>{t('common.howItWorks')}</Link>
          </li>
          <li>
            <Link to="/#pricing" onClick={(e: React.MouseEvent) => handleSectionClick(e, 'pricing')}>{t('common.pricing')}</Link>
          </li>
          <li><Link to="/blog" onClick={() => setMobileMenuOpen(false)}>{t('common.blog')}</Link></li>
          <li>
            <Link to="/#contact" onClick={(e: React.MouseEvent) => handleSectionClick(e, 'contact')}>{t('header.contact')}</Link>
          </li>
          {/* Mobile-only items */}
          <li className="lp-mobile-only">
            {!isAuthenticated && <Link to="/login" className="lp-btn-ghost lp-mobile-login" id="nav-login-mobile" onClick={() => setMobileMenuOpen(false)}>{t('common.logIn')}</Link>}
          </li>
          <li className="lp-mobile-only">
            {!isAuthenticated && <Link to="/signup" className="lp-btn-primary lp-mobile-signup" id="nav-signup-mobile" onClick={() => { setMobileMenuOpen(false); trackSignupClick('nav_mobile'); }}>{t('common.startFree')}</Link>}
          </li>
          <li className="lp-mobile-only lp-mobile-lang">
            <LanguageSwitcher />
          </li>
        </ul>

        <div className="lp-navbar-actions">
          <LanguageSwitcher />
          {isAuthenticated ? (
            <Link to="/dashboard" className="lp-btn-primary" id="nav-dashboard">{t('common.dashboard')}</Link>
          ) : (
            <>
              <Link to="/login" className="lp-btn-ghost" id="nav-login">{t('common.logIn')}</Link>
              <Link to="/signup" className="lp-btn-primary" id="nav-signup" onClick={() => trackSignupClick('nav')}>{t('common.startFree')}</Link>
            </>
          )}
        </div>

        <button className="lp-mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label={t('header.toggleMenu')}>
          <span /><span /><span />
        </button>
      </div>
    </nav>
  );
}
